import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RouteComponentProps, useHistory } from 'react-router';
import { compose } from '@reduxjs/toolkit';
import QueryString from 'qs';
import History, * as H from 'history';
import { Card, Grid, Header1Text, NavigationItem, Notification, usePrevious } from 'ui-toolkit';
import { DeleteJournalEntryModalContainer } from '../../components/accounting/DeleteJournalEntryModalContainer';
import { EditJournalEntryModalContainer } from '../../components/accounting/EditJournalEntryModalContainer';
import { loadJournalEntriesAction } from '../../store/accounting/slices/journalEntriesSlice';
import { loadJournalEntryRevisionsAction } from '../../store/accounting/slices/journalEntryLogsSlice';
import { IEscrowJournalEntryQuery } from '../../types/accounting/query/IEscrowJournalEntryQuery';
import { JournalEntriesDataGrid } from '../../components/accounting/JournalEntriesDataGrid';
import { createDateRangeFromDays } from '../../utilities/accounting/createDateRangeFromDays';
import { TDateRangeQueryValue } from '../../types/shared/TDateRangeQueryValue';
import { convertPaginationQueryStringToPaginationQuery } from '../../types/rest/IPaginationQuery';
import { convertSortQueryStringToSortQuery } from '../../types/rest/ISortQuery';
import { JournalEntriesNavigation } from '../../components/accounting/JournalEntriesNavigation';
import { NewJournalEntryModalContainer } from '../../components/accounting/NewJournalEntryModalContainer';
import { loadBankAccountList } from '../../store/accounting/slices/bankAccountListSlice';
import { eJournalEntryPaymentMethod } from '../../types/accounting/eJournalEntryPaymentMethod';
import { eJournalEntryType } from '../../types/accounting/eJournalEntryType';
import { IEscrowJournalEntryData } from '../../types/accounting/data/IEscrowJournalEntryData';
import { useDoActionOnceBasedOnFetchStatus } from '../../utilities/accounting/hooks/useDoActionOnceBasedOnFetchStatus';
import { eEntityRevisionType } from '../../types/accounting/eEntityRevisionType';
import { RootStore } from '../../store/types';
import { ePermissions } from 'types/administration/userAdministration/ePermissions';
import { useDocumentTitleEffect } from '../../utilities/hooks/useDocumentTitleEffect';
import { PermissionWrapper } from '../../components/accessControl/PermissionWrapper';
import { PermissionNotification } from '../../components/accessControl/PermissionNotification';
import { useHasPermissions } from '../../components/accessControl/hooks/useHasPermissions';
import { useAppDispatch } from '../../store/store';
import { useHasFeatureFlag } from '../../selectors/useHasFeatureFlag';

const SIZE_PER_PAGE = 25;

const convertLocationToQueryStringParams = (location: History.Location): QueryString.ParsedQs =>
  QueryString.parse(location.search, { ignoreQueryPrefix: true });

const convertQueryStringParamsToQuery = (
  queryStringParams: QueryString.ParsedQs
): Partial<IEscrowJournalEntryQuery> => {
  const {
    amountCents,
    completedAtDate,
    createdAt,
    bankAccountId,
    fileNumber,
    initiatedAtDate,
    note,
    paymentMethod,
    entryType,
  } = queryStringParams;
  return {
    amountCents: typeof amountCents === 'string' && !isNaN(parseInt(amountCents)) ? parseInt(amountCents) : undefined,
    completedAtDate: Array.isArray(completedAtDate) ? (completedAtDate as TDateRangeQueryValue) : undefined,
    createdAt: Array.isArray(createdAt) ? (createdAt as TDateRangeQueryValue) : undefined,
    entryType:
      typeof entryType === 'string' && Object.values(eJournalEntryType).includes(entryType as eJournalEntryType)
        ? (entryType as eJournalEntryType)
        : undefined,
    bankAccountId: typeof bankAccountId === 'string' ? bankAccountId : undefined,
    fileNumber: typeof fileNumber === 'string' ? fileNumber : undefined,
    initiatedAtDate: Array.isArray(initiatedAtDate) ? (initiatedAtDate as TDateRangeQueryValue) : undefined,
    note: typeof note === 'string' ? note : undefined,
    paymentMethod:
      typeof paymentMethod === 'string' &&
      Object.values(eJournalEntryPaymentMethod).includes(paymentMethod as eJournalEntryPaymentMethod)
        ? (paymentMethod as eJournalEntryPaymentMethod)
        : undefined,
    ...convertPaginationQueryStringToPaginationQuery(queryStringParams),
    ...convertSortQueryStringToSortQuery(queryStringParams),
  };
};

const applyDefaultQuery = ({
  completedAtDate,
  createdAt,
  initiatedAtDate,
  pageNumber,
  pageSize,
  sortField,
  sortOrder,
  ...rest
}: Partial<IEscrowJournalEntryQuery>): IEscrowJournalEntryQuery => ({
  completedAtDate,
  createdAt,
  initiatedAtDate: initiatedAtDate
    ? initiatedAtDate
    : !completedAtDate && !createdAt
      ? createDateRangeFromDays(-60)
      : undefined, // if there is no other date filter default to 60 days
  pageNumber: pageNumber || 1,
  pageSize: pageSize || SIZE_PER_PAGE,
  sortField: sortField || 'initiatedAtDate',
  sortOrder: sortOrder || 'desc',
  ...rest,
});

const initializeQueryState = compose(
  applyDefaultQuery,
  convertQueryStringParamsToQuery,
  convertLocationToQueryStringParams
);

const pushQueryToHistory = <T extends {} = {}>(history: H.History, query: T) =>
  history.push(`?${QueryString.stringify(query, { arrayFormat: 'indices', format: 'RFC3986' })}`);

export const JournalEntriesPage: React.FC<RouteComponentProps> = ({ location }) => {
  const hasReadPermission = useHasPermissions([ePermissions.EscrowJournalEntriesRead]);
  const hasEditPermission = useHasPermissions([ePermissions.EscrowJournalEntriesEdit]);
  const dispatch = useAppDispatch();
  const { bankAccountList, journalEntries, journalEntryLogs } = useSelector((state: RootStore) => state.accounting);
  const {
    fetchStatus: bankAccountsFetchStatus,
    fetchError: bankAccountsFetchError,
    data: bankAccounts,
  } = bankAccountList;
  const [query, setQuery] = useState(initializeQueryState(location));
  const history = useHistory();
  const [showNewModal, setShowNewModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [journalEntryBeingEdited, setJournalEntryBeingEdited] = useState<IEscrowJournalEntryData | undefined>();
  const [deleteJournalEntryId, setDeleteJournalEntryId] = useState<string | undefined>();
  const showDeleteJournalEntryModal = !!deleteJournalEntryId;
  const isShowingDeleted = location.pathname.endsWith('deleted');
  const [dateColumn, setDateColumn] = useState<'createdAt' | 'initiatedAtDate' | 'completedAtDate'>(
    !!query.createdAt ? 'createdAt' : !!query.completedAtDate ? 'completedAtDate' : 'initiatedAtDate'
  );
  const previousDateColumn = usePrevious(dateColumn);
  const isSpruceWithZillowEnabled = useHasFeatureFlag('pinecone.spruce-with-zillow-closing');

  // FYI: whenever there is an external location change update the query state
  useEffect(
    () =>
      history.listen((newLocation) => {
        setQuery(initializeQueryState(newLocation));
      }),
    [history]
  );

  const reloadJournalEntriesList = useCallback(() => {
    if (hasReadPermission) {
      if (isShowingDeleted) {
        const { escrowFileId, fileNumber, bankAccountId, sortField, ...revisionQuery } = query;
        const illegalSortFieldsForLogs = ['escrowFile.depositEscrowBankAccount.nickname', 'escrowFile.order.fileNumber'];
        dispatch(
          loadJournalEntryRevisionsAction({
            revisionType: eEntityRevisionType.DEL,
            sortField: sortField && illegalSortFieldsForLogs.includes(sortField) ? '' : sortField,
            ...revisionQuery,
          })
        );
      } else dispatch(loadJournalEntriesAction(query));
    }
  }, [hasReadPermission, dispatch, query, isShowingDeleted]);

  useDoActionOnceBasedOnFetchStatus(() => dispatch(loadBankAccountList()), bankAccountsFetchStatus);

  // FYI: load new results whenever the query was updated "a little while" ago
  useEffect(() => {
    reloadJournalEntriesList();
  }, [reloadJournalEntriesList]);

  useEffect(() => {
    setShowEditModal(!!journalEntryBeingEdited);
  }, [journalEntryBeingEdited]);

  useEffect(() => {
    if (!showEditModal) setJournalEntryBeingEdited(undefined);
  }, [showEditModal]);

  useDocumentTitleEffect('Journal Entries');

  useEffect(() => {
    //if it's not first render and dateColumn has actually changed this render
    if (!!previousDateColumn && previousDateColumn !== dateColumn) {
      let { completedAtDate, createdAt, initiatedAtDate, ...newQuery } = query;
      switch (dateColumn) {
        case 'completedAtDate':
          (newQuery as IEscrowJournalEntryQuery).completedAtDate =
            previousDateColumn === 'createdAt' ? createdAt : initiatedAtDate;
          break;
        case 'createdAt':
          (newQuery as IEscrowJournalEntryQuery).createdAt =
            previousDateColumn === 'completedAtDate' ? completedAtDate : initiatedAtDate;
          break;
        case 'initiatedAtDate':
          (newQuery as IEscrowJournalEntryQuery).initiatedAtDate =
            previousDateColumn === 'completedAtDate' ? completedAtDate : createdAt;
          break;
      }
      const isSortingByDate =
        !!newQuery.sortField && ['completedAtDate', 'createdAt', 'initiatedAtDate'].includes(newQuery.sortField);
      newQuery.sortField = isSortingByDate ? dateColumn : newQuery.sortField;
      newQuery.pageNumber = 1;
      pushQueryToHistory(history, newQuery);
    }
  }, [previousDateColumn, dateColumn, history, query]);

  return (
    <PermissionWrapper allowedPermissions={[ePermissions.EscrowJournalEntriesRead]}>
      <PermissionNotification allowedPermissions={[ePermissions.EscrowJournalEntriesEdit]} />
      <Card title={<Header1Text>{isShowingDeleted && 'Deleted'} Journal Entries</Header1Text>}>
        <Grid>
          <Grid templateColumns="1fr max-content" alignItems="center">
            <JournalEntriesNavigation
              disableCreate={!hasEditPermission}
              isShowingDeleted={isShowingDeleted}
              queryString={location.search}
              onNew={() => setShowNewModal(true)}
            />
            {/*
            FYI: there is a strange state management bug in DataGrid.
            when defaultValue is null the underlying SelectFilter state is not cleared
            so we are using a real anchor because the fresh page load will clear local state
            TODO: fix DataGrid defaultValue state bug
            */}
            <NavigationItem
              appearance="primary"
              intent="ghost"
              isInline={true}
              text={'Reset Search'}
              href={`?${QueryString.stringify(applyDefaultQuery({}), { arrayFormat: 'indices', format: 'RFC3986' })}`}
            />
          </Grid>
          {document.cookie.includes('debug') && <code>{JSON.stringify(query, undefined, '\t')}</code>}
          {bankAccountsFetchError && (
            <Notification
              type="warning"
              message="There was an error getting a bank account list for the Bank Account filter."
            />
          )}
          <JournalEntriesDataGrid
            bankAccountOptions={
              bankAccountsFetchStatus.fetched && bankAccountsFetchStatus.success
                ? bankAccounts.map(({ id, nickname, legalName, spruceName }) => {
                    const base = isSpruceWithZillowEnabled ? legalName : spruceName;
                    const label = nickname ? `${base} ${nickname}` : base;
                    return { label, value: id };
                  })
                : []
            }
            dateColumn={dateColumn}
            onClickEditButton={(journalEntry: IEscrowJournalEntryData) => setJournalEntryBeingEdited(journalEntry)}
            onDateColumnChange={setDateColumn}
            onDeleteClick={setDeleteJournalEntryId}
            isShowingDeleted={isShowingDeleted}
            journalEntries={isShowingDeleted ? journalEntryLogs : journalEntries}
            query={query}
            setQuery={(query) => pushQueryToHistory(history, query)}
          />
        </Grid>
      </Card>
      {showNewModal && (
        <NewJournalEntryModalContainer onClose={() => setShowNewModal(false)} onSuccess={reloadJournalEntriesList} />
      )}
      {showEditModal && journalEntryBeingEdited && (
        <EditJournalEntryModalContainer
          journalEntryId={journalEntryBeingEdited.id}
          onClose={() => setShowEditModal(false)}
          onSuccess={reloadJournalEntriesList}
        />
      )}
      {showDeleteJournalEntryModal && deleteJournalEntryId && (
        <DeleteJournalEntryModalContainer
          journalEntryId={deleteJournalEntryId}
          onClose={() => setDeleteJournalEntryId(undefined)}
          onSuccess={() => {
            //If this is the only item on screen and it's not page 1, change query to the previous page
            if (journalEntries.data.content.length === 1)
              pushQueryToHistory(history, { ...query, pageNumber: Math.max(1, (query.pageNumber || 0) - 1) });
            else reloadJournalEntriesList();
          }}
        />
      )}
    </PermissionWrapper>
  );
};
